import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Completed } from "../AdminIcons";
import FormSelect from "../common/FormSelect";
import { Clock3, Star } from "lucide-react";
import { useGetStudentProgressQuery } from "../../services/student-progress/studentProgressApi";




const StudentOverview = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const { state } = useLocation();
  const student = state?.student;
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const {
    data: progressResponse,
    isFetching,
    isError,
  } = useGetStudentProgressQuery(studentId, {
    skip: !studentId,
  });

  const courseProgressList = progressResponse?.data ?? [];

  useEffect(() => {
    if (!selectedCourseId && courseProgressList.length > 0) {
      const initialCourseId =
        student?.courseId || courseProgressList[0]?.courseId;
      if (initialCourseId) {
        setSelectedCourseId(String(initialCourseId));
      }
    }
  }, [courseProgressList, selectedCourseId, student?.courseId]);

  const activeCourseProgress = useMemo(
    () =>
      courseProgressList.find(
        (course) => String(course.courseId) === String(selectedCourseId),
      ) || courseProgressList[0],
    [courseProgressList, selectedCourseId],
  );

  const courseOptions = useMemo(
    () => [
      { label: "Filter by course", value: "" },
      ...courseProgressList.map((course) => ({
        label: course.courseTitle || "Untitled course",
        value: String(course.courseId),
      })),
    ],
    [courseProgressList],
  );

  const progressStats = activeCourseProgress?.stats;
  const progressPercentage = progressStats?.progressPercentage ?? 0;
  const completedModules = progressStats?.completedModules ?? 0;
  const totalModules = progressStats?.totalModules ?? 0;

  const studentName = student?.name || "Student";
  const studentEmail = student?.email || "-";
  const enrolledCourse =
    activeCourseProgress?.courseTitle || student?.course || "-";
  const enrollmentType = activeCourseProgress?.enrollmentType || "-";
  const enrollmentDate = student?.enrollmentDate || "-";
  const initials = studentName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

 

  return (
    <section className="min-h-screen bg-[#F5F5F5] px-5 xl:px-7 2xl:pl-14 2xl:pr-5 pb-5 pt-5 sm:pt-7 font-['Roboto']">
      <div className="max-w-[1448px] rounded-xl bg-white px-5 sm:px-7 pb-5 sm:pb-8 pt-4">
        <button
          type="button"
          aria-label="Go back"
          className="text-[#4A4A68]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft />
        </button>

        <h2 className="mt-2 text-xl font-medium leading-5 text-black">
          Student Progress Report
        </h2>

        {isFetching ? (
          <p className="mt-3 text-sm text-[#6B7280]">
            Loading student progress...
          </p>
        ) : null}

        {isError ? (
          <p className="mt-3 text-sm text-[#DC2626]">
            Unable to load student progress. Please try again.
          </p>
        ) : null}

        <div className="mt-5 flex  justify-between items-center gap-6 max-w-[600px]">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-[#1F2937] text-sm sm:text-base font-medium sm:font-semibold text-white">
              {initials}
            </div>
            <p className="text-lg sm:text-xl font-medium leading-5 text-black">
              {studentName}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-base text-black hidden sm:block">Course</span>
            <FormSelect
              id="student-progress-course"
              options={courseOptions}
              value={selectedCourseId}
              placeholder="Filter by course"
              onChange={setSelectedCourseId}
              className="w-[150px]"
              selectClassName="rounded-lg border border-[#938A96] px-3  sm:px-4 py-2 sm:py-2.5 text-sm  text-black"
              dropdownClassName="left-0 right-auto mt-1 min-w-[140px] rounded-md border border-[#E5E7EB] bg-white py-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
              optionClassName="px-3 py-1 text-sm text-[#374151] hover:bg-[#F3F4F6]"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 max-w-[1448px] rounded bg-white px-5 sm:px-8 pt-5 sm:pt-6 pb-6 sm:pb-8">
        <h3 className="text-lg sm:text-xl font-semibold tracking-[6%] leading-[23px] text-black">
          Student Information
        </h3>
        <p className="mt-2 sm:mt-2.5 text-base tracking-[1%] leading-[19px] text-[#6B6B6B]">
          Overview of the student&apos;s enrollment details
        </p>

        <div className="mt-5 flex items-center gap-4 sm:gap-6">
          <div className="flex h-20 sm:h-[91px] w-20 sm:w-[91px] items-center justify-center rounded-full bg-[#1F2937] text-xl sm:text-2xl font-semibold text-white">
            {initials}
          </div>
          <div>
            <p className="text-lg sm:text-xl font-medium  tracking-[1%] leading-[23px] text-black">
              {studentName}
            </p>
            <p className="sm:mt-1 text-base sm:text-lg font-medium leading-[21px] tracking-[1%] text-[#8C8CA1]">
              {studentEmail}
            </p>
          </div>
        </div>

        <div className="text-base sm:text-lg text-black font-medium tracking-[1%] leading-[21px] mt-3 sm:mt-5 flex gap-32 sm:gap-40">
          <div className="space-y-4 sm:space-y-5">
            <div>
              <p className="text-[#6B6B6B]">Learning Path</p>
              <p>{enrollmentType}</p>
            </div>
            <div>
              <p className="text-[#6B6B6B]">Enrollment Date</p>
              <p>{enrollmentDate}</p>
            </div>
          </div>
          <div className="space-y-4 sm:space-y-5">
            <div>
              <p className="text-[#6B6B6B]">Enrolled Course</p>
              <p>{enrolledCourse}</p>
            </div>

            <div>
              <p className="text-[#6B6B6B]">Valid Untill</p>
              <p>Aug 5, 2025</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-6 max-w-[1448px] rounded bg-white pt-5 sm:pt-6 pb-6 sm:pb-8  px-5 sm:px-6">
        <h3 className="text-lg sm:text-xl tracking-[6%] font-semibold leading-[23px] text-black">
          Course Progress Summary
        </h3>
        <p className=" sm:mt-2.5 text-base leading-[19px] text-[#6B6B6B]">
          Overall progress in the enrolled course
        </p>

        <div className="mt-5 font-medium text-sm sm:text-lg tracking-[1%] text-black flex items-center justify-between">
          <p>Module Progress</p>
          <p>
            {completedModules}/{totalModules} Modules Completed
          </p>
        </div>

        <div className="mt-2 sm:mt-4 h-3 sm:h-[15px] w-full rounded-full bg-[#D2D2D2]">
          <div
            className="h-3 sm:h-[15px] rounded-full bg-[#923D8C]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="mt-8 sm:mt-[52px] max-w-[908px] grid grid-cols-1  sm:grid-cols-2">
          <div className="rounded-lg border border-[#923D8C] bg-[#923D8C12] p-5 sm:px-[30px]  sm:pb-7 sm:pt-5">
            <p className="text-[22px] sm:text-2xl leading-7  text-[#000]">
              Course Completion
            </p>
            <p className="mt-4 sm:mt-6 text-[28px] sm:text-[32px] font-semibold leading-[38px] text-[#923D8C]">
              {progressPercentage}%
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StudentOverview;
